'use client';

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon, SearchIcon, SquareTerminalIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTimeout } from 'usehooks-ts';
import { AI_DISCLOSURE_LABEL_CLASS, AI_DISCLOSURE_TRAILING_ICON_CLASS, AI_DISCLOSURE_TRIGGER_CLASS } from '@/components/ai-elements/disclosure-styles';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
type ToolTraceSummary = {
  toolCount: number;
  toolNames: string[];
};
type ToolTraceGroupProps = {
  summary: ToolTraceSummary;
  children: ReactNode;
};
const TOOL_TRACE_AUTO_CLOSE_DELAY = 1000;
const TOOL_TRACE_CONTENT_TRANSITION = {
  duration: 0.2,
  ease: 'easeOut'
} as const;
function formatTraceSummary({
  toolCount,
  toolNames
}: ToolTraceSummary): string {
  const unique = [...new Set(toolNames)];
  const limit = 3;
  if (unique.length === 0) {
    return `Explored · ${toolCount} ${toolCount === 1 ? 'action' : 'actions'}`;
  }
  const shown = unique.slice(0, limit).join(', ');
  const remaining = unique.length - limit;
  if (remaining > 0) {
    return `Explored ${shown} +${remaining} more · ${toolCount} ${toolCount === 1 ? 'action' : 'actions'}`;
  }
  return `Explored ${shown} · ${toolCount} ${toolCount === 1 ? 'action' : 'actions'}`;
}
function getTraceIcon(summary: ToolTraceSummary) {
  const uniqueToolNames = new Set(summary.toolNames);

  // Bash traces are effectively terminal sessions inside the read-only PR sandbox,
  // so use the terminal glyph instead of the generic exploration glyph.
  return uniqueToolNames.size === 1 && uniqueToolNames.has('Bash') ? {
    Icon: SquareTerminalIcon,
    testId: 'tool-trace-terminal-icon'
  } : {
    Icon: SearchIcon,
    testId: 'tool-trace-search-icon'
  };
}
export function ToolTraceGroup(t0) {
  const $ = _c(38);
  const {
    summary,
    children
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== summary) {
    t1 = getTraceIcon(summary);
    $[0] = summary;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    Icon: TraceIcon,
    testId: traceIconTestId
  } = t1;
  const [open, setOpen] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOpen(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useTimeout(t2, open && !hasUserInteracted ? TOOL_TRACE_AUTO_CLOSE_DELAY : null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = nextOpen => {
      setHasUserInteracted(true);
      setOpen(nextOpen);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpenChange = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(AI_DISCLOSURE_TRIGGER_CLASS);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== TraceIcon || $[6] !== traceIconTestId) {
    t5 = <TraceIcon data-testid={traceIconTestId} className="size-4 text-muted-foreground/70" />;
    $[5] = TraceIcon;
    $[6] = traceIconTestId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== summary) {
    t6 = formatTraceSummary(summary);
    $[8] = summary;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <span className={AI_DISCLOSURE_LABEL_CLASS}>{t6}</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = open && "rotate-90";
  let t9;
  if ($[12] !== t8) {
    t9 = cn(AI_DISCLOSURE_TRAILING_ICON_CLASS, "opacity-100 transition-transform duration-200 ease-out motion-reduce:transition-none", t8);
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <ChevronRightIcon className={t9} />;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t5 || $[18] !== t7) {
    t11 = <CollapsibleTrigger className={t4}>{t5}{t7}{t10}</CollapsibleTrigger>;
    $[16] = t10;
    $[17] = t5;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = !open;
  const t13 = !open;
  let t14;
  if ($[20] !== open || $[21] !== shouldReduceMotion) {
    t14 = shouldReduceMotion ? {
      height: open ? "auto" : 0,
      opacity: 1,
      y: 0,
      marginTop: open ? 4 : 0
    } : {
      height: open ? "auto" : 0,
      opacity: open ? 1 : 0,
      y: open ? 0 : -6,
      marginTop: open ? 4 : 0
    };
    $[20] = open;
    $[21] = shouldReduceMotion;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== shouldReduceMotion) {
    t15 = shouldReduceMotion ? {
      duration: 0
    } : TOOL_TRACE_CONTENT_TRANSITION;
    $[23] = shouldReduceMotion;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = !open && "pointer-events-none";
  let t17;
  if ($[25] !== t16) {
    t17 = cn("space-y-0 overflow-hidden [&_.group/disclosure]:mb-0 [&>*:last-child]:mb-0", t16);
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== children || $[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t15 || $[32] !== t17) {
    t18 = <CollapsibleContent forceMount={true} asChild={true}><motion.div aria-hidden={t12} initial={false} inert={t13} animate={t14} transition={t15} className={t17}>{children}</motion.div></CollapsibleContent>;
    $[27] = children;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== open || $[35] !== t11 || $[36] !== t18) {
    t19 = <Collapsible className="group/disclosure not-prose mb-3 box-border min-w-0 w-full max-w-full" open={open} onOpenChange={handleOpenChange}>{t11}{t18}</Collapsible>;
    $[34] = open;
    $[35] = t11;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
}